import { ref } from 'vue';
import { useModal } from 'vue-final-modal';
import {
  ExternalDocumentUploadModal,
  ReplaceInternalDocumentModal,
  ReplaceExternalDocumentModal,
  useFileUploadStore,
} from '@/features/Document';
import { isExternalDocument, type DocumentRevision } from '@/entities/Document';

export function useDocumentUploadModals() {
  const fileUploadStore = useFileUploadStore();
  const { deleteDocuments } = fileUploadStore;
  const replacementRevision = ref<DocumentRevision | undefined>(undefined);

  const resetUploadState = () => {
    fileUploadStore.files = [];
    fileUploadStore.hasFiles = false;
    fileUploadStore.filesLoaded = false;
    fileUploadStore.uploadedDocumentIds = [];
    fileUploadStore.fileToDocumentIdMap.clear();
  };

  const { open: openExternalUpload, close: closeExternalUpload } = useModal({
    component: ExternalDocumentUploadModal,
    attrs: {
      title: 'Upload de Documentos Externos (DE)',
      isReplacement: false,
      documentId: 0,
      async onClose(documentIds: number[] | undefined) {
        if (documentIds) {
          deleteDocuments(documentIds);
        }
        resetUploadState();
        closeExternalUpload();
      },
    },
  });

  const { open: openReplaceInternal, close: closeReplaceInternal } = useModal({
    component: ReplaceInternalDocumentModal,
    attrs: {
      title: 'Substituir Documento',
      get documentId() {
        return replacementRevision.value?.documentId ?? 0;
      },
      get revisionId() {
        return replacementRevision.value?.id ?? 0;
      },
      get documentPrefix() {
        return replacementRevision.value?.document?.prefix ?? '';
      },
      get isUrlDocument() {
        return !!replacementRevision.value?.docUrl;
      },
      onClose() {
        closeReplaceInternal();
        resetUploadState();
        replacementRevision.value = undefined;
      },
    },
  });

  const { open: openReplaceExternal, close: closeReplaceExternal } = useModal({
    component: ReplaceExternalDocumentModal,
    attrs: {
      title: 'Substituir Documento',
      get documentId() {
        return replacementRevision.value?.documentId ?? 0;
      },
      get revisionId() {
        return replacementRevision.value?.id ?? 0;
      },
      get isUrlDocument() {
        return !!replacementRevision.value?.docUrl;
      },
      onClose() {
        closeReplaceExternal();
        resetUploadState();
        replacementRevision.value = undefined;
      },
    },
  });

  const openExternalUploadModal = () => {
    openExternalUpload();
  };

  const openReplaceModal = (revision: DocumentRevision) => {
    replacementRevision.value = revision;
    const isExternal = isExternalDocument(revision);
    if (isExternal) {
      openReplaceExternal();
    } else {
      openReplaceInternal();
    }
  };

  return {
    openExternalUploadModal,
    openReplaceModal,
  };
}
